import axios from 'axios';
import { Authentication } from '@icabbi/vue-authentication';

const defaultState = {
  list: [],
  triggerBuildInProgress: false,
  listBuildsInProgress: false,
  onboardingApiServiceClient: null,
  buildDetailsLoading: false,
  buildDetails: [],
};

const getters = {
  list: state => state.list,
  triggerBuildInProgress: state => state.triggerBuildInProgress,
  listBuildsInProgress: state => state.listBuildsInProgress,
  onboardingApiServiceClient: (state, contextGetters, rootState, rootGetters) => ({ dispatch }) => {
    const authentication = Authentication({ dispatch, getters: rootGetters });
    const { authenticationFailureInterceptor, bearerTokenInterceptor } = authentication;
    if (state.onboardingApiServiceClient === null) {
      state.onboardingApiServiceClient = axios.create({
        baseURL: process.env.VUE_APP_ONBOARDING_SERVICE_BASE_URL,
        timeout: 30000,
      });
      state.onboardingApiServiceClient.interceptors.response.use(
        undefined, error => authenticationFailureInterceptor(error),
      );
      state.onboardingApiServiceClient.interceptors.request.use(
        config => bearerTokenInterceptor(config),
      );
    }
    return state.onboardingApiServiceClient;
  },
  buildDetailsLoading: state => state.buildDetailsLoading,
  buildDetails: state => state.buildDetails,
};

const actions = {
  async list(context, { platform }) {
    try {
      context.commit('setListBuildsInProgress', true);
      const { data } = await context.getters.onboardingApiServiceClient(context)
        .get('/app-builds', { params: { platform } });
      context.commit('setBuildsList', data);
    } catch (error) {
      context.dispatch(
        'globalMessages/addError',
        { message: 'builds.listBuildsError', error },
        { root: true },
      );
    } finally {
      context.commit('setListBuildsInProgress', false);
    }
  },
  async loadBuildDetails(context, { platform, build }) {
    try {
      context.commit('setBuildDetailsLoading', true);
      const { data } = await context.getters.onboardingApiServiceClient(context)
        .get(`/app-builds/${platform}/${build.id}/jobs`);
      context.commit('setBuildDetails', data);
    } catch (error) {
      context.dispatch(
        'globalMessages/addError',
        { message: 'builds.buildDetailsError', error },
        { root: true },
      );
    } finally {
      context.commit('setBuildDetailsLoading', false);
    }
  },
  async trigger(context, {
    bookingChannel, platform, paymentEnv, buildVersion,
  }) {
    try {
      context.commit('setTriggerBuildInProgress', true);
      await context.getters.onboardingApiServiceClient(context)
        .post('/app-builds', {
          bookingChannelId: bookingChannel.id,
          platform,
          paymentEnv,
          buildVersion,
        });
      context.dispatch(
        'globalMessages/addSuccess',
        { message: 'builds.buildTriggeredSuccess' },
        { root: true },
      );
    } catch (error) {
      context.dispatch(
        'globalMessages/addError',
        { message: 'builds.triggerBuildsError', error },
        { root: true },
      );
    } finally {
      context.commit('setTriggerBuildInProgress', false);
    }
  },
};

const mutations = {
  setBuildsList(state, builds) {
    state.list = builds;
  },
  setTriggerBuildInProgress(state, triggerBuildInProgress) {
    state.triggerBuildInProgress = triggerBuildInProgress;
  },
  setListBuildsInProgress(state, listBuildsInProgress) {
    state.listBuildsInProgress = listBuildsInProgress;
  },
  setBuildDetails(state, buildDetails) {
    state.buildDetails = buildDetails;
  },
  setBuildDetailsLoading(state, buildDetailsLoading) {
    state.buildDetailsLoading = buildDetailsLoading;
  },
};

export default {
  namespaced: true,
  state: defaultState,
  getters,
  actions,
  mutations,
};
